/**
 * Expandable Text Mixin
 * Provides functionality for text that can be expanded/collapsed when it exceeds a certain number of lines
 */
export default {
    data() {
        return {
            expandedStates: {},
            shouldTruncate: {},
            maxLines: 8, // Maximum lines before truncation
        }
    },
    methods: {
        /**
         * Toggle the expanded state of a rewards text element
         * @param {number} index - The card index
         */
        toggleRewardsText: function(index) {
            const key = `rewards${index}`;
            const newState = !this.expandedStates[key];
            this.$set(this.expandedStates, key, newState);
            
            // Update the max-height for smooth animation
            const element = this.$refs[`rewardsText${index}`];
            const el = Array.isArray(element) ? element[0] : element;
            
            if (el) {
                if (newState) {
                    // Expanding: set to scroll height for smooth transition
                    el.style.maxHeight = el.scrollHeight + 'px';
                } else {
                    // Collapsing: first set to scroll height, then to collapsed height
                    el.style.maxHeight = el.scrollHeight + 'px';
                    // Force reflow then set to collapsed height
                    el.offsetHeight; // trigger reflow
                    const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 20;
                    el.style.maxHeight = (this.maxLines * lineHeight) + 'px';
                }
            }
        },

        /**
         * Check if text elements need truncation and set up initial states
         */
        checkTextHeight: function() {
            this.$nextTick(() => {
                this.cards.forEach((card, index) => {
                    const element = this.$refs[`rewardsText${index}`];
                    const el = Array.isArray(element) ? element[0] : element;
                    
                    if (el) {
                        // Temporarily remove height restriction to measure full height
                        el.style.maxHeight = 'none';
                        el.style.transition = 'none'; // Disable transition during measurement
                        const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 20;
                        const maxHeight = this.maxLines * lineHeight;
                        const actualHeight = el.scrollHeight;
                        
                        const key = `rewards${index}`;
                        if (actualHeight > maxHeight) {
                            this.$set(this.shouldTruncate, key, true);
                            this.$set(this.expandedStates, key, false);
                            // Set initial collapsed height
                            el.style.maxHeight = maxHeight + 'px';
                        } else {
                            this.$set(this.shouldTruncate, key, false);
                            el.style.maxHeight = 'none';
                        }
                        
                        // Re-enable transitions after a brief delay
                        setTimeout(() => {
                            el.style.transition = '';
                        }, 10);
                    }
                });
            });
        },

        /**
         * Get rewards text without the "best" indicator
         * @param {string} value - The text value
         * @returns {string} The text or 'N/A' if empty
         */
        getRewardsTextOnly: function (value) {
            return value ? value : 'N/A';
        },

        /**
         * Get rewards value with optional "best" indicator
         * @param {string} value - The text value
         * @param {boolean} best - Whether this is the best option
         * @returns {string} The formatted text with optional "best" indicator
         */
        getRewardsVal: function (value, best) {
            if(best) {
                return value ? value + ' <span class="best"> <span class="lt-Check"></span>Best</span>' : 'N/A';
            } else {
                return value ? value : 'N/A';
            }
        },

        /**
         * Get fee value with fallback text
         * @param {string} value - The fee value
         * @returns {string} The fee value or 'See Terms' if empty
         */
        getFeeVal: function (value) {
            return value ? value : 'See Terms';
        }
    },
    watch: {
        cards: {
            handler: function(newCards, oldCards) {
                // Check if cards were added (length increased)
                if (newCards.length > (oldCards ? oldCards.length : 0)) {
                    // Wait for DOM to update, then check text heights
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.checkTextHeight();
                        }, 100);
                    });
                }
            },
            immediate: false
        }
    },
    mounted() {
        // Check text heights after component is mounted
        setTimeout(() => {
            this.checkTextHeight();
        }, 500);
    }
};
